/*
 * <read-more-toggle> — collapses a long block of rich text to a fixed height
 * and reveals a "Read more" button only when the content actually overflows.
 *
 * The clamp itself lives in CSS (scoped to html.js) so there is no layout
 * shift on load. Without JavaScript the text renders in full and the button
 * stays hidden, so no content is ever unreachable.
 */
if (!customElements.get('read-more-toggle')) {
  customElements.define(
    'read-more-toggle',
    class ReadMoreToggle extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector('[data-readmore-content]');
        this.button = this.querySelector('[data-readmore-button]');
        if (!this.content || !this.button) return;

        this.labelMore = this.querySelector('[data-readmore-label-more]');
        this.labelLess = this.querySelector('[data-readmore-label-less]');

        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);

        this.sync();

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.sync());
          this.resizeObserver.observe(this.content);
        } else {
          this.onResize = this.sync.bind(this);
          window.addEventListener('resize', this.onResize);
        }
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
      }

      get expanded() {
        return this.classList.contains('is-expanded');
      }

      /* Show the button only when there is genuinely hidden text. */
      sync() {
        if (this.expanded) return;
        const overflows = this.content.scrollHeight - this.content.clientHeight > 4;
        this.classList.toggle('is-overflowing', overflows);
        this.button.hidden = !overflows;
      }

      onClick() {
        const willExpand = !this.expanded;

        this.classList.toggle('is-expanded', willExpand);
        this.button.setAttribute('aria-expanded', String(willExpand));

        if (this.labelMore) this.labelMore.hidden = willExpand;
        if (this.labelLess) this.labelLess.hidden = !willExpand;

        /* Collapsing from far down the page would leave the reader stranded
           below the fold, so pull the top of the text back into view. */
        if (!willExpand) {
          if (this.getBoundingClientRect().top < 0) {
            this.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
          this.sync();
        }
      }
    }
  );
}
